import { ResourceLoadingUtils } from 'app/authoring/authoring-main/resource-loading-utils';
import { ScopeOnSynapseUtils } from 'app/extension/api/scope-on-synapse-utils';
import { loggerConstants } from 'app/logger/logger.constants';
import { PerformanceEvent } from 'app/logger/logger.performance-event';
import { LoggerService } from 'app/logger/logger.service';
import { routingConstants } from 'app/shared/constants/routing.const';
import { of } from 'rxjs';
import { timeout } from 'rxjs/operators';
import {
    IOwnedViolation,
    Notebook,
    ScopeJobDefinition,
    SqlScript,
    KqlScript,
    SparkJobDefinition,
    ISettingViolation,
    IPublishViolations,
    Credential,
    IModel,
    ModelMarker,
    IResourceBase,
    ModelConstants,
    MODEL_SERVICE_LIST_TIMEOUT,
    Database,
    Table,
    Relationship,
    SparkConfiguration,
    LinkTopic,
    IValidationContext
} from 'shell-models-lib';
import * as Constants from 'app/services/globals';
import { DataFlow } from '../dataflow/dataflow';
import { Dataset } from '../dataset/dataset';
import { IntegrationRuntime } from '../integrationRuntime/integrationRuntime';
import { LinkedService } from '../linkedservice/linkedservice';
import { Pipeline } from '../pipeline/pipeline';
import { ModelService } from '../services/model.service';
import { ArmTemplateUtils } from '../services/publisher/armTemplate.utils';
import { Trigger } from '../trigger/trigger';
import { Metamodel } from './model';
import { Named, Owned } from './named';
import { Validator } from './validator';
import { WranglingDataflow } from '../dataflow/wranglingDataflow';
import { WorkspaceUtils } from '../services/model.utils/workspace.utils';
import { PrimaryResourceUrlInformationService } from 'app/services/primary-resource-url-information.service';
import { ColumnType } from 'app/shared/utils/column-type.util';
import {
    irDefaultInstanceName,
    irNameMaxLength,
    irNameMinLength,
    irNamePattern
} from 'app/integrationruntime/integrationruntime.constants';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { A365WorkspaceCacheService } from 'app/services/metadata-client/A365workspace-cache.service';
import { AdfDataMapper } from '../adfDataMapper/adfDataMapper';
import { IResourceContext } from 'app/multi-contexts/resource-context.typings';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { convertObjectToString, format, sanitizeAndFormatString } from 'app/shared/utils/string.util';

export class ValidatorUtils {
    private static readonly className: string = 'ValidatorUtils';
    private static a365NonComputeModels: string[] = [
        ModelConstants.notebook,
        ModelConstants.sqlScript,
        ModelConstants.kqlScript,
        ModelConstants.sparkJobDefinition,
        ModelConstants.scopeJobDefinition,
        ModelConstants.database,
        ModelConstants.table,
        ModelConstants.relationship,
        ModelConstants.sparkConfiguration
    ];

    public static async validatePublish(
        contextData: IResourceContext,
        modelService: ModelService,
        isGitMode: boolean,
        changedResources?: IResourceBase[]
    ): Promise<IPublishViolations> {
        return {
            resourceViolations:
                (await ValidatorUtils.validateAllModels(
                    modelService,
                    {
                        contextData,
                        isPublish: true,
                        changedResources
                    },
                    isGitMode
                )) || [],
            settingsViolations: (await ValidatorUtils.validateFactorySettings(contextData, isGitMode)) || []
        };
    }

    public static async validatePublishForNameds(
        namedSet: Named[],
        isGitMode: boolean,
        context: IValidationContext
    ): Promise<IPublishViolations> {
        const resourceViolations = await Validator.validatePublish(true, context, ...namedSet);
        const settingsViolations = isGitMode
            ? await ValidatorUtils.validateFactorySettings(context.contextData, isGitMode)
            : [];
        if ((resourceViolations && resourceViolations.length) || settingsViolations.length) {
            return {
                resourceViolations,
                settingsViolations
            };
        }
        return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static async matchDefaultStorageLsJson(resource: any, context: IValidationContext): Promise<boolean> {
        const workspaceServiceObj = await getServiceInstance<A365WorkspaceCacheService>(
            context.contextData,
            'A365WorkspaceCacheService'
        );

        const workspaceName = workspaceServiceObj.workspace.name;
        const defaultStorageAccountUrl = workspaceServiceObj.workspace.properties.defaultDataLakeStorage.accountUrl;
        const defaultSqlConnectionString = `${WorkspaceUtils.defaultSqlServerConnectionStringPrefix}${workspaceName}${WorkspaceUtils.defaultSqlServerConnectionStringSuffix}`;
        const defaultLinkedServiceResourceType = `${Constants.WORKSPACE_FULLRESOURCE_TYPE}/${WorkspaceUtils.linkedServicesSuffix}`;
        const integrationRuntimeReference = ReferenceAdapter.toType(Metamodel.modelFor(IntegrationRuntime));

        return resource.properties.type === ModelConstants.adlsGen2LinkedService
            ? resource.name === `${workspaceName}${WorkspaceUtils.defaultStorageLinkedServiceSuffix}` &&
                  resource.type === defaultLinkedServiceResourceType &&
                  resource.properties.typeProperties.url === defaultStorageAccountUrl &&
                  resource.properties.connectVia.referenceName === irDefaultInstanceName &&
                  resource.properties.connectVia.type === integrationRuntimeReference &&
                  resource.properties.annotations.length === 0
            : resource.name === `${workspaceName}${WorkspaceUtils.defaultSqlServerLinkedServiceSuffix}` &&
                  resource.type === defaultLinkedServiceResourceType &&
                  resource.properties.typeProperties.connectionString === defaultSqlConnectionString &&
                  resource.properties.parameters.DBName.type === ColumnType.String &&
                  resource.properties.type === ModelConstants.synapseLinkedService &&
                  resource.properties.connectVia.referenceName === irDefaultInstanceName &&
                  resource.properties.connectVia.type === integrationRuntimeReference &&
                  resource.properties.annotations.length === 0;
    }

    // This method checks if the changed resources contains any default linked services, and if they do, returns violations.
    public static async checkDefaultLinkedServiceStatus(
        namedModels: Named[],
        context: IValidationContext
    ): Promise<IOwnedViolation[] | undefined> {
        const defaultLinkedServiceViolations: IOwnedViolation[] = [];
        const primaryResrcUrlInfoServiceObj = await getServiceInstance<PrimaryResourceUrlInformationService>(
            context.contextData,
            'PrimaryResourceUrlInformationService'
        );

        for (const namedModel of namedModels) {
            const owned = namedModel as Owned;

            if (
                WorkspaceUtils.isWorkspaceDefaultLinkedService(
                    Metamodel.modelOf(namedModel).name,
                    namedModel.name,
                    primaryResrcUrlInfoServiceObj.service.resourceName
                )
            ) {
                // check this only if anything is changed during publish.
                if (context.changedResources != null) {
                    context.changedResources.filter(async (changedResource) => {
                        // check if a default LS is changed.
                        if (
                            namedModel.name === changedResource.name &&
                            Metamodel.modelFor(LinkedService).name === changedResource.type
                        ) {
                            const resource = (namedModel as LinkedService).cachedResource;
                            // check if the payload has changed.
                            if (!(await ValidatorUtils.matchDefaultStorageLsJson(resource, context))) {
                                // This is a bad resource.
                                defaultLinkedServiceViolations.push({
                                    owned,
                                    context: owned.named,
                                    message: () => stringResources.LinkedService_DefaultLsChanged_ErrorMessage,
                                    logDetails: {
                                        functionName: 'ValidatorUtils.checkDefaultLinkedServiceStatus',
                                        errorType: 'Readonly'
                                    },
                                    attribute: null,
                                    corrected$: of(null)
                                });
                            }
                        }
                    });
                }
            }
        }
        return defaultLinkedServiceViolations;
    }

    public static async validateAllModels(
        modelService: ModelService,
        context: IValidationContext,
        isGitMode?: boolean
    ): Promise<IOwnedViolation[]> {
        const startTime = performance.now();
        const models: IModel[] = [];
        models.push(
            Metamodel.modelFor(Pipeline),
            Metamodel.modelFor(Dataset),
            Metamodel.modelFor(Trigger),
            Metamodel.modelFor(DataFlow),
            Metamodel.modelFor(LinkedService),
            Metamodel.modelFor(IntegrationRuntime),
            Metamodel.modelFor(WranglingDataflow)
        );
        models.push(Metamodel.modelFor(AdfDataMapper));
        if (__IS_A365__) {
            models.push(
                Metamodel.modelFor(Notebook),
                Metamodel.modelFor(KqlScript),
                Metamodel.modelFor(SqlScript),
                Metamodel.modelFor(SparkJobDefinition)
            );
            if (ScopeOnSynapseUtils.isSosEnabled()) {
                models.push(Metamodel.modelFor(ScopeJobDefinition));
            }
            models.push(Metamodel.modelFor(Database));
            models.push(Metamodel.modelFor(Table));
            models.push(Metamodel.modelFor(Relationship));
            models.push(Metamodel.modelFor(SparkConfiguration));
        }
        if (__IS_A365__ && Constants.GlobalVariables.enableSynapseLink) {
            models.push(Metamodel.modelFor(LinkTopic));
        }
        models.push(Metamodel.modelFor(Credential));
        let nameds = ((await modelService
            .list(undefined, ...models)
            .pipe(timeout(MODEL_SERVICE_LIST_TIMEOUT))
            .toPromise()) || []) as Named[];
        // below only affect a365 non-compute models: notebook, sqlscript and sparkjobdef
        if (!isGitMode && __IS_A365__ && nameds && nameds.length) {
            nameds = nameds.filter((n) => {
                // include if not such model
                if (!ValidatorUtils.a365NonComputeModels.includes(Metamodel.modelOf(n).name)) {
                    return true;
                }
                // include only if new/dirty
                return ModelMarker.isNew(n) || ModelMarker.isDirty(n);
            });
        }

        if (!nameds) {
            return [];
        }
        const namedModels = await Promise.all(nameds.map(async (named: Named) => await modelService.fetch(named)));
        const violations: IOwnedViolation[] = [];

        // Push the resources which marked bad into violations.
        for (const namedModel of namedModels) {
            const owned = namedModel as Owned;
            if (!owned.named.isOk) {
                // This is a bad resource.
                violations.push({
                    owned,
                    context: owned.named,
                    message: () => ResourceLoadingUtils.getResourceErrorDetails(owned.named),
                    logDetails: {
                        functionName: 'ValidatorUtils.validateAllModels',
                        errorType: 'Other',
                        details: {
                            loadError: owned.named.loadError,
                            name: owned.named.name
                        }
                    },
                    attribute: null,
                    corrected$: of(null)
                });
            }
        }

        // Limit this validation to ADF cmd-api; preserve Synapse and UI behavior.
        if (!__IS_A365__ && Constants.GlobalVariables.isCmdApiApp) {
            violations.push(...ValidatorUtils.validateIntegrationRuntimeNames(namedModels));
        }

        // Validate models which are not bad resource
        const currViolations = await ValidatorUtils.validateModelsExtensively(
            namedModels,
            { ...context, ...modelService.modelAttributeContext },
            modelService.factory
        );
        violations.push(...currViolations);

        const loggerService = await getServiceInstance<LoggerService>(context.contextData, 'LoggerService');
        loggerService.logEvent(
            new PerformanceEvent(
                ValidatorUtils.className,
                loggerConstants.Operation_ValidateAll,
                performance.now() - startTime,
                nameds.length.toString()
            )
        );

        return violations;
    }

    public static async validateModelsExtensively(
        namedModels: Named[],
        context: IValidationContext,
        factory?: Named
    ): Promise<IOwnedViolation[]> {
        // Do the extensive validation for pipeline, dataset, dataflow and triggers, which are not bad resource.
        // We ignore the extensive validation of IRs, as those are already published.
        // now we add linkedservice validation, as linkedservices with no credential also haven't been published yet.
        const modelNames = [
            Metamodel.modelFor(Pipeline).name,
            Metamodel.modelFor(Dataset).name,
            Metamodel.modelFor(Trigger).name,
            Metamodel.modelFor(DataFlow).name,
            Metamodel.modelFor(WranglingDataflow).name,
            Metamodel.modelFor(LinkedService).name,
            Metamodel.modelFor(AdfDataMapper).name
        ];

        if (__IS_A365__) {
            modelNames.push(Metamodel.modelFor(Notebook).name);
            modelNames.push(Metamodel.modelFor(SqlScript).name);
            modelNames.push(Metamodel.modelFor(KqlScript).name);
            modelNames.push(Metamodel.modelFor(SparkJobDefinition).name);
            if (ScopeOnSynapseUtils.isSosEnabled()) {
                modelNames.push(Metamodel.modelFor(ScopeJobDefinition).name);
            }
            modelNames.push(Metamodel.modelFor(Database).name);
            modelNames.push(Metamodel.modelFor(Table).name);
            modelNames.push(Metamodel.modelFor(Relationship).name);
        }
        if (__IS_A365__ && Constants.GlobalVariables.enableSynapseLink) {
            modelNames.push(Metamodel.modelFor(LinkTopic).name);
        }

        modelNames.push(Metamodel.modelFor(Credential).name);

        const namedsForExtensiveValidation = namedModels.filter(
            (named) => named.isOk && modelNames.includes(Metamodel.modelOf(named).concept.name)
        );
        if (Constants.GlobalVariables.enableGlobalParameters) {
            namedsForExtensiveValidation.push(factory);
        }
        const currViolations = await (context.isPublish
            ? Validator._validate(true, context, ...namedsForExtensiveValidation)
            : Validator.validate(true, context, ...namedsForExtensiveValidation));

        // The following function checks if any of the default linked services have been modified as part of validation for a365.
        if (__IS_A365__) {
            const defaultLinkedServiceChangedViolations = await ValidatorUtils.checkDefaultLinkedServiceStatus(
                namedModels,
                context
            );
            currViolations.push(...defaultLinkedServiceChangedViolations);
        }

        return currViolations;
    }

    public static async validateFactorySettings(
        contextData: IResourceContext,
        isGitMode: boolean
    ): Promise<ISettingViolation[]> {
        const violations: ISettingViolation[] = [];
        if (isGitMode) {
            try {
                await ArmTemplateUtils.getArmTemplateParameterDefinition(contextData, false);
            } catch (error) {
                violations.push({
                    message: () => stringResources.ParameterizationTemplateValidationError,
                    logDetails: {
                        functionName: 'ValidatorUtils.validateFactorySettings',
                        errorType: 'Other',
                        details: {
                            error: convertObjectToString(error)
                        }
                    },
                    icon: 'svg-i-20x20-parameterization-template icon',
                    name: stringResources.ParameterizationTemplate,
                    route: `${routingConstants.managementPath}/${routingConstants.armtemplate}`,
                    doNotAutomaticallyCorrect: true
                });
            }
        }
        return violations;
    }

    public static validateIntegrationRuntimeNames(namedModels: Named[]): IOwnedViolation[] {
        // Use the same IR name rules as the ADF creation screen for command-line validation.
        // Check only the name, without changing shared rules or validating other IR settings.
        const modelNameAttribute = Metamodel.modelFor(IntegrationRuntime).attribute('name');
        const namePattern = new RegExp(`(?=^.{${irNameMinLength},${irNameMaxLength}}$)${irNamePattern}`);
        const violations: IOwnedViolation[] = [];

        for (const namedModel of namedModels) {
            if (!namedModel.isOk || !(namedModel instanceof IntegrationRuntime)) {
                continue;
            }

            const name = namedModel.name;
            if (typeof name === 'string' && namePattern.test(name)) {
                continue;
            }

            const isMissingName = typeof name !== 'string' || name.trim().length === 0;
            violations.push({
                owned: namedModel,
                context: namedModel,
                message: () => isMissingName
                    ? format(stringResources.Validator_NameRequired, stringResources.IntegrationRuntime)
                    : sanitizeAndFormatString(
                        stringResources.PatternValidatorMessage,
                        stringResources.IntegrationRuntime,
                        modelNameAttribute.display,
                        name,
                        namePattern.toString()
                    ),
                messageType: isMissingName ? 'missing' : undefined,
                logDetails: {
                    functionName: 'ValidatorUtils.validateIntegrationRuntimeNames',
                    errorType: isMissingName ? 'Required' : 'Pattern'
                },
                attribute: modelNameAttribute,
                corrected$: of(null)
            });
        }

        return violations;
    }
}
